// Standalone admin page — routed at #/admin, not a tab inside the board or
// resident shells. Sign-in gates everything; the page owns its own scroll so
// the fixed footer never overlaps it.

import React, { useEffect, useState } from "react";
import { useAdmin } from "./useAdmin.js";
import { CommunityDetailPage, CommunityFilter, CommunityList, MetricRow, OnboardWizard } from "./adminParts.jsx";
import AdminFooter, { FOOTER_HEIGHT } from "./AdminFooter.jsx";
import AdminLogin from "./AdminLogin.jsx";
import { Button, Toast } from "./ui.jsx";
import "./admin/admin.css";

const linkStyle = {
  border: "1px solid var(--rule2)",
  color: "var(--muted2)",
  textDecoration: "none",
  padding: "10px 13px",
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  font: "600 10px/1 var(--bq-font-display)",
  letterSpacing: ".1em",
  textTransform: "uppercase",
};

// #/admin/communities/{id} -- a real page per community, not just an
// inline expand under the list. Parsed independently of App.jsx's own
// top-level router, since AdminApp is mounted whole for any #/admin* hash.
const parseDetailId = () => {
  const m = location.hash.match(/^#\/admin\/communities\/([^/]+)\/?$/);
  return m ? decodeURIComponent(m[1]) : null;
};

export default function AdminApp() {
  const admin = useAdmin();
  const [session, setSession] = useState(null);
  const [detailId, setDetailId] = useState(parseDetailId);

  useEffect(() => {
    const onHash = () => setDetailId(parseDetailId());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Keeps useAdmin's selection (and therefore admin.selected/detailCards)
  // in sync with whatever the URL says, so a direct link or a page refresh
  // lands on the right community, not just a click from the list.
  useEffect(() => {
    admin.selectId(detailId);
  }, [detailId, admin.selectId]);

  const openCommunity = (id) => { location.hash = "/admin/communities/" + encodeURIComponent(id); };
  const backToCommunities = () => { location.hash = "/admin"; };

  return (
    <div
      style={{
        height: "100vh",
        boxSizing: "border-box",
        paddingBottom: `calc(5vh + ${FOOTER_HEIGHT}px)`,
        background: "var(--page)",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          flex: 1,
          maxWidth: 1180,
          minWidth: 0,
          minHeight: 0,
          position: "relative",
          background: "var(--bg)",
          borderLeft: "1px solid var(--ink)",
          borderRight: "1px solid var(--ink)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <header
          style={{
            flex: "none",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "clamp(16px, 2.4vw, 26px) clamp(14px, 2.4vw, 26px) 14px",
            borderBottom: "2px solid var(--ink)",
          }}
        >
          <div style={{ width: 16, height: 16, background: "var(--accent)", flex: "none" }} />
          <h1 style={{ margin: 0, flex: 1, minWidth: 0, font: "800 clamp(24px, 3.6vw, 36px)/0.94 var(--bq-font-display)", letterSpacing: "-0.03em" }}>
            Administration
          </h1>
          {session && (
            <nav style={{ display: "flex", alignItems: "center", gap: 8, flex: "none", flexWrap: "wrap" }}>
              <Button onClick={admin.openWizard} style={{ minHeight: 40, padding: "11px 15px" }}>Onboard community</Button>
              <a href="#/" style={linkStyle}>Landing</a>
              <a href="#/communities" style={linkStyle}>Communities</a>
              <button onClick={() => setSession(null)} style={{ ...linkStyle, background: "transparent", cursor: "pointer" }}>
                Sign out
              </button>
            </nav>
          )}
        </header>

        {!session ? (
          <AdminLogin onSuccess={setSession} />
        ) : (
          <>
            {detailId ? (
              <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", padding: "clamp(14px, 2.2vw, 22px) clamp(14px, 2.2vw, 22px) 32px" }}>
                <CommunityDetailPage
                  community={admin.selected}
                  cards={admin.detailCards}
                  loading={admin.communities.length === 0}
                  onBack={backToCommunities}
                />
              </div>
            ) : (
              <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", padding: "clamp(14px, 2.2vw, 22px) clamp(14px, 2.2vw, 22px) 32px" }}>
                <MetricRow metrics={admin.metricsTop} />
                <MetricRow metrics={admin.metricsBottom} tone="muted" />

                <CommunityFilter
                  filter={admin.filter}
                  onFilter={admin.setFilter}
                  status={admin.status}
                  onStatus={admin.setStatus}
                  options={admin.STATUS_FILTERS}
                  count={`${admin.list.length} ${admin.list.length === 1 ? "community" : "communities"}`}
                />

                <CommunityList items={admin.list} selectedId={admin.selectedId} onSelect={openCommunity} />
              </div>
            )}

            <OnboardWizard admin={admin} />
          </>
        )}

        <Toast text={admin.toast} />
      </div>

      <AdminFooter />
    </div>
  );
}
